'use client'

import { createPortal } from 'react-dom'
import { useEffect, useState } from 'react'
import Link from 'next/link'

import { Button, OverlayArrow, Tooltip, TooltipTrigger } from 'react-aria-components'

export default function AdminHeaderBar({ post, user }: { post: any; user: any }) {
  const [isCollapsed, setIsCollapsed] = useState(false)
  const [portalTarget, setPortalTarget] = useState<HTMLElement | null>(null)

  useEffect(() => {
    if (typeof window !== 'undefined') {
      const collapsed = localStorage.getItem('admin-header-collapsed')

      if (collapsed === 'true') {
        setIsCollapsed(true)
      }
    }

    setPortalTarget(document.body)

    const adminHeaderEl = document.getElementById('admin-header')
    if (adminHeaderEl) {
      adminHeaderEl.style.height = '32px'
    }

    // The onboarding modal adds the `inert` attribute to sibling elements,
    // which would make the admin bar non-interactive. Strip it from the
    // admin bar whenever it is applied.
    const inertObserver = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (mutation.attributeName !== 'inert') continue
        const target = mutation.target as HTMLElement
        if (target.querySelector('.admin-header') || target.classList.contains('admin-header')) {
          target.removeAttribute('inert')
        }
      }
    })

    inertObserver.observe(document.body, {
      attributes: true,
      attributeFilter: ['inert'],
      subtree: true,
    })

    return () => {
      inertObserver.disconnect()
      if (adminHeaderEl) {
        adminHeaderEl.style.height = ''
      }
    }
  }, [])

  const handleCollapse = () => {
    setIsCollapsed(!isCollapsed)

    if (typeof window !== 'undefined') {
      localStorage.setItem('admin-header-collapsed', isCollapsed ? 'false' : 'true')
    }
  }

  const {
    id: postId = null,
    revision_parent: parentId = null,
    term_id: termId = null,
    taxonomy: taxonomy = null,
  } = post || {}

  const cmsUrl = process.env.NEXT_PUBLIC_CMS_URL
  const adminUrl = `${cmsUrl}/wp-admin`

  // Determine edit ID based on whether post is a revision
  const isRevision = post?.type === 'revision'
  const editId = isRevision ? parentId : postId

  const userEmail = user?.email || ''
  const simulatedUserEmail = user?.meta?.simulated_user_email || null

  const cmsSvg = (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 32 32"
      fill="currentColor"
      className="h-[16px] w-[16px]">
      <path d="M4 16c0 4.626 2.76 8.624 6.763 10.518L5.039 11.244A11.381 11.381 0 0 0 4 16zm20.101-.59c0-1.445-.533-2.445-.989-3.223-.608-.963-1.179-1.778-1.179-2.741 0-1.074.836-2.074 2.015-2.074.053 0 .104.006.155.009A12.134 12.134 0 0 0 16 4.312c-4.192 0-7.881 2.095-10.026 5.268.282.009.547.014.772.014 1.255 0 3.198-.149 3.198-.149.647-.037.723.889.077.963 0 0-.65.074-1.373.111l4.37 12.66 2.626-7.671-1.869-4.989c-.646-.037-1.259-.111-1.259-.111-.647-.037-.571-1 .076-.963 0 0 1.981.149 3.16.149 1.255 0 3.198-.149 3.198-.149.647-.037.723.889.076.963 0 0-.651.074-1.373.111l4.337 12.563 1.197-3.895c.518-1.617.913-2.778.913-3.778zm-7.89 1.612L12.61 27.211a12.29 12.29 0 0 0 7.375-.187 1.028 1.028 0 0 1-.086-.161l-3.688-9.842zm10.319-6.629c.052.372.081.771.081 1.202 0 1.185-.228 2.519-.913 4.186l-3.665 10.321C25.601 24.076 28 20.312 28 16c0-2.032-.533-3.943-1.47-5.607zM16 0C7.163 0 0 7.163 0 16s7.163 16 16 16 16-7.163 16-16S24.837 0 16 0zm0 30C8.268 30 2 23.732 2 16S8.268 2 16 2s14 6.268 14 14-6.268 14-14 14z" />
    </svg>
  )

  const editSvg = (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 32 32"
      fill="currentColor"
      className="h-[16px] w-[16px]">
      <path d="M27 0a5 5 0 0 1 4 8l-2 2-7-7 2-2c.836-.628 1.874-1 3-1zM2 23l-2 9 9-2 18.5-18.5-7-7L2 23zm20.362-11.638-14 14-1.724-1.724 14-14 1.724 1.724z" />
    </svg>
  )

  const userSvg = (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 32 32"
      fill="currentColor"
      className="h-[16px] w-[16px]">
      <path d="M18 22.082v-1.649c2.203-1.241 4-4.337 4-7.432 0-4.971 0-9-6-9s-6 4.029-6 9c0 3.096 1.797 6.191 4 7.432v1.649C7.216 22.637 2 25.97 2 30h28c0-4.03-5.216-7.364-12-7.918z" />
    </svg>
  )

  const renderAdminHeader = (
    <div className="pointer-events-none fixed top-0 left-0 z-[110] w-full font-[-apple-system,BlinkMacSystemFont,Roboto,Oxygen-Sans,Ubuntu,Cantarell,sans-serif] text-[13px] text-[#c3c4c7]">
      <div
        className={`admin-header pointer-events-auto relative z-[990] w-full overflow-visible bg-[#1d2327] px-xs ${isCollapsed ? 'hidden' : ''}`}>
        <div className="flex h-[32px]">
          <a
            href={adminUrl}
            target="_blank"
            className="inline-flex items-center gap-xxxs px-xxs hover:bg-[#2c3338] hover:text-[#72aee6]">
            {cmsSvg}
            Jersey Finance
          </a>

          {!termId && (postId || parentId) ? (
            <a
              href={`${adminUrl}/post.php?post=${editId}&action=edit`}
              target="_blank"
              className="inline-flex items-center gap-xxxs px-xxs hover:bg-[#2c3338] hover:text-[#72aee6]">
              {editSvg}
              Edit Page
            </a>
          ) : null}

          {termId && taxonomy ? (
            <a
              href={`${adminUrl}/wp-admin/term.php?taxonomy=${taxonomy}&tag_ID=${termId}`}
              target="_blank"
              className="inline-flex items-center gap-xxxs px-xxs hover:bg-[#2c3338] hover:text-[#72aee6]">
              {editSvg}
              Edit Category
            </a>
          ) : null}

          <div className="ml-auto inline-flex items-center gap-xxxs pr-[32px]">
            {userSvg}

            <span className="text-white">
              <Link href={`${process.env.NEXT_PUBLIC_CMS_URL}/wp-admin/profile.php`}>
                {userEmail}
              </Link>
            </span>
            {simulatedUserEmail ? (
              <>
                {' '}
                viewing as{' '}
                <TooltipTrigger delay={0}>
                  <Button>
                    <Link
                      href={`${process.env.NEXT_PUBLIC_CMS_URL}/wp-json/jf-simulate-user/v1/restore-session`}
                      className="text-white">
                      {simulatedUserEmail}
                    </Link>
                  </Button>
                  <Tooltip>
                    <OverlayArrow>
                      <svg width={8} height={8} viewBox="0 0 8 8">
                        <path d="M0 0 L4 4 L8 0" />
                      </svg>
                    </OverlayArrow>
                    Click to restore session
                  </Tooltip>
                </TooltipTrigger>
              </>
            ) : null}
          </div>
        </div>
      </div>

      <button
        onClick={() => handleCollapse()}
        className={`pointer-events-auto absolute right-0 top-0 z-[999] ml-auto inline-flex h-[32px] w-[45px] items-center justify-center gap-xxxs rounded-bl-md bg-[#1d2327] px-xxs text-[#c3c4c7]`}>
        {isCollapsed ? (
          <span className="dashicons dashicons-arrow-down-alt2"></span>
        ) : (
          <span className="dashicons dashicons-arrow-up-alt2"></span>
        )}
      </button>
    </div>
  )

  if (!portalTarget) return null

  return <>{createPortal(renderAdminHeader, portalTarget)}</>
}
